import type { ScanSignalDto, StockRowDto, StreamEventDto } from '@pl/domain';
import { type Query, type QueryKey, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { noteCountSignal, noteStreamGap } from '../scan/pages/countKit';
import { type CountsQuery, qk } from './queries';
import { heartbeatMs, openStream, silentAfter, type StreamStatus } from './stream';

/** Events kept in memory for the feed, newest first. */
export const FEED_MAX = 100;
/**
 * How long a refetch waits for company. A `resync` rarely comes alone — the server's listener
 * reconnecting sends one to every screen, and a count starting sends one as its first message — and
 * each would otherwise refetch every query on the page.
 */
const REFRESH_DEBOUNCE_MS = 300;
/**
 * How long invalidations collect before they are sent. Every invalidation cancels the fetch in flight
 * and starts another, so a burst — confirming a 50-box shipment writes 50 `box.shipped` and an
 * `order.shipped`; a replay after a Wi-Fi drop can be 500 — would fire one request per message. Held
 * this long, and deduplicated, the burst refetches each query once. Well inside the 2 s budget.
 */
const INVALIDATE_BATCH_MS = 75;

interface Live {
  /** What the stream is open for: a site id, `all`, or null for no stream. */
  site: string | null;
  status: StreamStatus;
  feed: StreamEventDto[];
}

const LiveContext = createContext<Live>({ site: null, status: 'connecting', feed: [] });

const isStockRow = (d: unknown): d is StockRowDto =>
  typeof d === 'object' && d !== null && typeof (d as StockRowDto).siteId === 'string' && typeof (d as StockRowDto).skuId === 'string';

/**
 * An event, in either shape: the office's full `StreamEventDto`, or the scan app's `ScanSignalDto`
 * (`surface=scan`), which carries only the id, type, site and the order, transfer or count ids. Every
 * rule below reads those fields alone, so both apps' caches follow the same events.
 */
type LiveEvent = Pick<ScanSignalDto, 'id' | 'type' | 'siteId'> & {
  orderId?: string | null;
  transferId?: string | null;
  countId?: string | null;
} & Partial<Omit<StreamEventDto, keyof ScanSignalDto>>;

const isEvent = (d: unknown): d is LiveEvent =>
  typeof d === 'object' && d !== null && typeof (d as LiveEvent).id === 'number' && typeof (d as LiveEvent).type === 'string';

/** The office's whole event, with who, when and what; the scan app's signal has none of it. */
const isFullEvent = (d: unknown): d is StreamEventDto => isEvent(d) && typeof d.at === 'string' && typeof d.payload === 'object' && d.payload !== null;

/**
 * A refused scan made while looking a box up. Somebody checking what a box is has not done anything
 * wrong, so it is no alert: the feed leaves it out, as the Wall does.
 */
const isLookup = (e: LiveEvent): boolean => e.type === 'scan.rejected' && e.payload?.mode === 'lookup';

/**
 * A live row replaces its pair, joins the list if the pair is new, and leaves it once all four of
 * its numbers are zero — the same shape `GET /stock` answers with, which drops all-zero pairs.
 */
function patchStock(list: StockRowDto[] | undefined, row: StockRowDto): StockRowDto[] | undefined {
  if (!list) return list;
  const at = list.findIndex((r) => r.siteId === row.siteId && r.skuId === row.skuId);
  const gone = row.inStock === 0 && row.atDock === 0 && row.reserved === 0 && row.available === 0;
  if (at === -1) return gone ? list : [...list, row];
  if (gone) return list.filter((_, i) => i !== at);
  const next = list.slice();
  next[at] = row;
  return next;
}

interface CacheOps {
  patchStock(key: QueryKey, row: StockRowDto): void;
  /**
   * A count started, finished or ended at this site: on the scan surface, what the phone held of the
   * site's counts is taken away, not only marked stale (see `atCountSite`).
   */
  dropCountResults(siteId: string): void;
  /** The same for every site: the stream stopped being live, and a count may have moved anywhere unheard (I-1). */
  dropAllCountResults(): void;
  /** Marks the queries under `key` stale — batched, see `INVALIDATE_BATCH_MS`. */
  invalidate(key: QueryKey): void;
}

/**
 * The cached queries that say how a site's counts went: full counts (a count's own, or a screen's
 * answer for it), the count lists the home's chips and links come from, and discrepancies — all of
 * them, since one is asked for by a count's id, not by its site. A full count whose site is not yet
 * known is taken too.
 */
const atCountSite = (siteId: string) => (q: Query) => {
  const [head, second] = q.queryKey;
  if (head === 'count') {
    const d = q.state.data as { siteId?: string; dto?: { siteId?: string } } | undefined;
    const at = d?.siteId ?? d?.dto?.siteId;
    return at === undefined || at === siteId;
  }
  if (head === 'counts') {
    const listed = (second as CountsQuery | undefined)?.siteId;
    return listed === undefined || listed === siteId;
  }
  return head === 'discrepancies';
};
/** Every site's: after a gap in the stream, nothing cached of any site's counts is known to be current. */
const atAnyCountSite = (q: Query) => q.queryKey[0] === 'count' || q.queryKey[0] === 'counts' || q.queryKey[0] === 'discrepancies';

/** What each kind of message means for the query cache. */
function applyToCache(ops: CacheOps, kind: string, data: unknown): void {
  if (kind === 'stock') {
    if (!isStockRow(data)) return;
    ops.patchStock(qk.stock('all'), data);
    ops.patchStock(qk.stock(data.siteId), data);
    ops.invalidate(['availability']);
    ops.invalidate(['stock-boxes', data.siteId, data.skuId]);
    return;
  }
  if (!isEvent(data)) return;
  if (kind === 'order' || (kind === 'box' && data.orderId)) {
    ops.invalidate(['orders']);
    if (data.orderId) ops.invalidate(qk.order(data.orderId));
  }
  // A box lookup can be keyed by the code as typed or as formatted, and a phone's signal names no
  // box: the prefix covers every lookup.
  if (kind === 'box') ops.invalidate(['box']);
  if (kind === 'label') ops.invalidate(qk.batches);
  if (kind === 'import') ops.invalidate(['skus']);
  // A box scanned onto or off a transfer moves its lines, as do the transfer's own events. Closing
  // one with boxes still on the road opens a discrepancy for each.
  if (kind === 'transfer' || (kind === 'box' && data.transferId)) {
    ops.invalidate(['transfers']);
    if (data.transferId) ops.invalidate(qk.transfer(data.transferId));
  }
  if (kind === 'transfer' && data.type === 'transfer.closed') ops.invalidate(['discrepancies']);
  // Which SKUs are being counted at a site: a count's own events, and a box or pallet moving onto a
  // counted pallet, which brings its SKU under the count.
  if (kind === 'count' || kind === 'box' || kind === 'pallet') ops.invalidate(['counts-counting']);
  // A count opening or ending changes which codes the server leaves out of an order or a transfer
  // on the scan surface (`codesHidden`): the ones on screen are asked for again.
  if (kind === 'count') {
    ops.invalidate(['order']);
    ops.invalidate(['transfer']);
  }
  // The lists and the suggestions are never on screen while a phone counts, so a count open on a
  // phone asks for nothing but itself here. Finishing opens its discrepancies.
  if (kind === 'count') {
    if (data.siteId) ops.dropCountResults(data.siteId);
    ops.invalidate(['counts']);
    ops.invalidate(['counts-suggested']);
    if (data.countId) {
      ops.invalidate(qk.count(data.countId));
      // The count screen's read: a joined count finished or canceled elsewhere is learned here.
      ops.invalidate(qk.countProgress(data.countId));
    }
    if (data.type === 'count.finished') ops.invalidate(['discrepancies']);
  }
  // A resolved discrepancy names the count or the transfer it came from.
  if (kind === 'discrepancy') {
    ops.invalidate(['discrepancies']);
    if (data.countId) ops.invalidate(qk.count(data.countId));
    if (data.transferId) {
      ops.invalidate(['transfers']);
      ops.invalidate(qk.transfer(data.transferId));
    }
  }
}

/**
 * Holds the stream open for one site (or `all`) while mounted, and keeps every cached query in step
 * with it: stock rows are patched in place, everything else a message touches is marked stale and
 * refetched if it is on screen.
 *
 * Two refetches are debounced. `resync` refetches everything: the server is saying it lost track.
 * And coming back live after a drop refetches the stock queries: the replay that follows a
 * reconnect carries the missed events but no `stock` rows, so the numbers would otherwise stay
 * where the drop left them. With no event seen before the drop there is nothing to replay from, so
 * that reconnect refetches everything instead.
 *
 * `site` is null while there is nothing to stream yet. The provider stays mounted either way, so a
 * site arriving does not remount everything under it.
 *
 * A stream refused with 401 means the credential is gone. `onUnauthorized` decides what to do; by
 * default who is signed in is asked again, and that 401 is what sends the app to the login screen.
 */
export function LiveProvider({
  site,
  surface,
  onUnauthorized,
  EventSourceImpl,
  children,
}: {
  site: string | null;
  /** `scan` for the phone's stream, which the server sends no stock rows (see `openStream`). */
  surface?: 'scan';
  onUnauthorized?: () => void;
  EventSourceImpl?: typeof EventSource;
  children: ReactNode;
}) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<StreamStatus>('connecting');
  const [feed, setFeed] = useState<StreamEventDto[]>([]);
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  useEffect(() => {
    // A new site starts a new feed; an empty one is left alone rather than replaced.
    setFeed((prev) => (prev.length ? [] : prev));
    setStatus('connecting');
    if (site === null) return;
    let everything = false;
    let stock = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const refresh = (all: boolean) => {
      if (all) everything = true;
      else stock = true;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        if (everything) void qc.invalidateQueries();
        else if (stock) {
          void qc.invalidateQueries({ queryKey: ['stock'] });
          void qc.invalidateQueries({ queryKey: ['stock-boxes'] });
          void qc.invalidateQueries({ queryKey: ['availability'] });
        }
        everything = false;
        stock = false;
      }, REFRESH_DEBOUNCE_MS);
    };

    // Keys to invalidate, by their serialized form, sent together once the batch window closes. The
    // window does not restart on each new key, so a steady trickle is still sent every 75 ms.
    const pending = new Map<string, QueryKey>();
    let batch: ReturnType<typeof setTimeout> | null = null;
    const ops: CacheOps = {
      dropCountResults: (siteId) => {
        if (surface !== 'scan') return;
        noteCountSignal(siteId);
        // Reset, not invalidate: the data goes at once and a request already on its way is dropped,
        // so nothing from before the count can be drawn; what is on screen is asked for again.
        void qc.resetQueries({ predicate: atCountSite(siteId) });
      },
      dropAllCountResults: () => {
        if (surface !== 'scan') return;
        noteStreamGap();
        void qc.resetQueries({ predicate: atAnyCountSite });
      },
      patchStock: (key, row) => {
        // A poll for this list already on its way was sent before this row existed, and would land
        // on top of it with older numbers. Cancelled first — synchronously, reverting to what was
        // there before it — its answer is dropped; the next interval asks again.
        void qc.cancelQueries({ queryKey: key, exact: true });
        qc.setQueryData<StockRowDto[]>(key, (list) => patchStock(list, row));
      },
      invalidate: (key) => {
        pending.set(JSON.stringify(key), key);
        batch ??= setTimeout(() => {
          batch = null;
          const keys = [...pending.values()];
          pending.clear();
          for (const queryKey of keys) void qc.invalidateQueries({ queryKey });
        }, INVALIDATE_BATCH_MS);
      },
    };

    let dropped = false;
    let live = false;
    /**
     * Whether any event has arrived. Until one has, there is no position to resume from: the browser
     * sends no Last-Event-ID, the server replays nothing, and whatever happened during the drop is
     * lost. Coming back in that state is a resync — everything is fetched again, not just stock.
     * A Wall reloaded at 03:00 starts every day this way.
     */
    let positioned = false;
    const close = openStream({
      site,
      surface,
      EventSourceImpl,
      onStatus: (s) => {
        if (s === 'reconnecting') dropped = true;
        // Leaving `live` — an error, or silence past the heartbeat — is a gap: fail closed on every
        // site's counts until lists asked for after it arrive. Once per drop; the screens poll meanwhile.
        if (live && s !== 'live') ops.dropAllCountResults();
        live = s === 'live';
        if (s === 'live' && dropped) {
          dropped = false;
          refresh(!positioned);
        }
        setStatus(s);
      },
      onMessage: (kind, data) => {
        if (kind === 'resync') return refresh(true);
        if (kind !== 'stock' && isEvent(data)) positioned = true;
        applyToCache(ops, kind, data);
        // The feed is the office's, and shows who did what: a phone's signal says neither.
        if (kind !== 'stock' && isFullEvent(data) && !isLookup(data)) {
          const e = data;
          // Newest first by id: a replay can deliver an older event after a newer one.
          setFeed((prev) => [...prev, e].sort((a, b) => b.id - a.id).slice(0, FEED_MAX));
        }
      },
      onUnauthorized: () => {
        if (onUnauthorizedRef.current) onUnauthorizedRef.current();
        else void qc.invalidateQueries({ queryKey: qk.me });
      },
    });

    // Back from a time away longer than one heartbeat and its margin (review m-1): the tab hidden, the
    // phone put away, the page frozen. Nothing it heard can be trusted for that long, and nothing was
    // polled: TanStack pauses a hidden tab's intervals, and another site's count sends this phone no
    // signal. So coming back is a gap, as a dropped stream is. A shorter time away is not.
    let awayFrom: number | null = typeof document !== 'undefined' && document.hidden ? Date.now() : null;
    const away = () => {
      awayFrom ??= Date.now();
    };
    const back = () => {
      if (awayFrom === null || document.hidden) return;
      const gone = Date.now() - awayFrom;
      awayFrom = null;
      if (gone > silentAfter(heartbeatMs())) ops.dropAllCountResults();
    };
    const onVisibility = () => (document.hidden ? away() : back());
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('freeze', away);
    document.addEventListener('resume', back);
    window.addEventListener('pagehide', away);
    window.addEventListener('pageshow', back);
    return () => {
      close();
      if (timer) clearTimeout(timer);
      if (batch) clearTimeout(batch);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('freeze', away);
      document.removeEventListener('resume', back);
      window.removeEventListener('pagehide', away);
      window.removeEventListener('pageshow', back);
    };
  }, [site, surface, qc, EventSourceImpl]);

  const value = useMemo(() => ({ site, status, feed }), [site, status, feed]);
  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

export const useLiveStatus = (): StreamStatus => useContext(LiveContext).status;

/** The site id the stream is open for, `all`, or null. */
export const useLiveSite = (): string | null => useContext(LiveContext).site;

/** The newest `limit` events this screen has seen since it opened the stream. */
export function useLiveFeed(limit: number): StreamEventDto[] {
  const { feed } = useContext(LiveContext);
  return useMemo(() => feed.slice(0, limit), [feed, limit]);
}
